import { vec2, vec4 } from "gl-matrix"
import {
	clamp,
	getLimitedDxDyForRotation,
	getLimitedDxDyWithSlidingEdge,
	getOutofBoundsPercent,
	getOutofBoundsPercentForIrregularRect,
	getOverlapCenter,
	getRotatedCrop,
	getTransformedImageVertices,
	lerp,
	rotatePoint,
} from "@/utils/math"
import { radians } from "@/core/utils/math"
import { Adjustments, Crop } from "@/core/types"
import {
	anchorPoints,
	Layer,
	layerToBounds,
	scaleLayer,
} from "@/components/canvas-controls/canvas-controls-utils"
import { getDefaultAdjustments } from "@/core/adjustments"
import equal from "fast-deep-equal"

export interface CropHandlerOptions {
	pointer: vec2
	pointerStart: vec2
	controlPoint: vec2
	imageSize: vec2
	keepAspect: boolean
	fromCenter: boolean
	adjustments: Adjustments
	initialOrientation: number
}

export const handleCropScale = (layer: Layer, options: CropHandlerOptions) => {
	// const originalLayer = layer
	// let updateOriginalLayer = false

	layer = { ...layer }

	const imageSize = options.imageSize
	const startPos = options.pointerStart
	const pos = options.pointer
	const delta = vec2.sub(vec2.create(), pos, startPos)

	const controlPoint: vec2 = vec2.clone(options.controlPoint)
	const oppositePoint: vec2 = [1 - controlPoint[0], 1 - controlPoint[1]]
	const origin: vec2 = options.fromCenter ? [0.5, 0.5] : oppositePoint
	const originPoint = origin.slice() as vec2

	// Calculate crop bounds
	const cropBounds = layerToBounds(layer)

	// Map origin point to crop point
	origin[0] = lerp(cropBounds.left, cropBounds.right, origin[0])
	origin[1] = lerp(cropBounds.top, cropBounds.bottom, origin[1])
	controlPoint[0] = lerp(cropBounds.left, cropBounds.right, controlPoint[0])
	controlPoint[1] = lerp(cropBounds.top, cropBounds.bottom, controlPoint[1])

	if (options.fromCenter) {
		delta[0] *= 0.5
		delta[1] *= 0.5
	}

	const point = vec2.add(vec2.create(), controlPoint, delta)

	const nx = point[0] - origin[0]
	const ny = point[1] - origin[1]

	let dx = controlPoint[0] - origin[0]
	let dy = controlPoint[1] - origin[1]

	// Get scale from origin point
	let sx = dx !== 0 ? nx / dx : 1
	let sy = dy !== 0 ? ny / dy : 1

	if (options.keepAspect) {
		const [x, y] = originPoint
		const isCorner = (x === 1 || x === 0) && (y === 1 || y === 0)
		const cropWidth = layer.width * imageSize[0]
		const cropHeight = layer.height * imageSize[1]
		const invCropRatio = cropHeight / cropWidth
		const cropRatio = cropWidth / cropHeight
		const threshold = lerp(cropRatio, invCropRatio, 0.75)
		const scaledRatio = Math.abs((cropWidth * sx) / (cropHeight * sy))

		const flipRatio = cropHeight > cropWidth ? scaledRatio > threshold : scaledRatio < threshold

		if (isCorner && flipRatio) {
			scaleLayer(layer, [cropHeight / cropWidth, cropWidth / cropHeight], origin)
			// updateOriginalLayer = true
		}

		dx = Math.abs(dx)
		dy = Math.abs(dy)
		sx = sy = (sx * dx + sy * dy) / (dx + dy)
	}

	// Set minimum scale
	sx = Math.max(0.01, sx)
	sy = Math.max(0.01, sy)

	scaleLayer(layer, [sx, sy], origin)

	// ====== CONSTRAIN CROP ======

	// Calculate image boundary overlap
	// (percent of crop region that is outside bounds)
	let scaleX = 0,
		scaleY = 0

	const adjustments = options.adjustments
	const straighten = adjustments.cropStraighten
	const hwRatio = imageSize[1] / imageSize[0]
	const hasTransform = hasNonZeroTransform(adjustments)

	if (hasTransform || Math.abs(straighten) >= 0.01) {
		const rotationAngle = radians(straighten)
		const initialBounds = layerToBounds(layer)

		const originX = lerp(initialBounds.left, initialBounds.right, originPoint[0])
		const originY = lerp(initialBounds.top, initialBounds.bottom, originPoint[1]) * hwRatio

		const startCrop: vec4 = [
			initialBounds.left,
			initialBounds.top * hwRatio,
			initialBounds.right,
			initialBounds.bottom * hwRatio,
		]

		let scales: vec2 = [0, 0]
		if (hasTransform) {
			const [left, top, right, bottom] = startCrop
			const cropVertices: vec2[] = [
				[left, top],
				[right, top],
				[right, bottom],
				[left, bottom],
			]

			const transformedImageVertices = getTransformedImageVertices(
				adjustments,
				imageSize,
				options.initialOrientation,
			)

			const imageCenter: vec2 = [0.5, 0.5 * hwRatio]
			const straightenedImageVertices = getRotatedCrop(
				[0, 0, 1, hwRatio],
				rotationAngle,
				imageCenter,
			)

			scales = getCropScalingPercent(
				cropVertices,
				[originX, originY],
				originPoint,
				transformedImageVertices,
				straightenedImageVertices,
				null,
				false,
			)
		} else {
			const imageBounds = [
				[0, 1],
				[0, hwRatio],
			]
			const rotatedCrop = getRotatedCrop(startCrop, -rotationAngle, [0.5, 0.5 * hwRatio])
			const rotatedOrigin = rotatePoint(originX, originY, -rotationAngle, [
				0.5,
				0.5 * hwRatio,
			])
			scales = getCropScalingPercent(
				rotatedCrop,
				rotatedOrigin,
				originPoint,
				null,
				null,
				imageBounds,
				true,
			)
		}
		scaleX = scales[0]
		scaleY = scales[1]
	} else {
		const bounds = layerToBounds(layer)
		const overlapTop = bounds.top / (bounds.top - bounds.bottom)
		const overlapLeft = bounds.left / (bounds.left - bounds.right)
		const overlapRight = (bounds.right - 1) / (bounds.right - bounds.left)
		const overlapBottom = (bounds.bottom - 1) / (bounds.bottom - bounds.top)

		scaleX = Math.max(0, overlapLeft, overlapRight)
		scaleY = Math.max(0, overlapTop, overlapBottom)

		if (options.controlPoint[0] === 0.5 || options.fromCenter) {
			scaleX *= 2
		}

		if (options.controlPoint[1] === 0.5 || options.fromCenter) {
			scaleY *= 2
		}
	}

	// Scale crop back to fit within bounds

	if (options.keepAspect) {
		scaleX = scaleY = Math.max(scaleX, scaleY)
	}

	if (scaleX || scaleY) {
		scaleLayer(layer, [1 - scaleX, 1 - scaleY], origin)
	}

	// ============================

	// This causes bug: control point no longer correct, leading to weird behavior
	// if (updateOriginalLayer) {
	// 	originalLayer.x = layer.x
	// 	originalLayer.y = layer.y
	// 	originalLayer.width = layer.width
	// 	originalLayer.height = layer.height
	// }

	return layer
}

export const handleCropMove = (layer: Layer, options: CropHandlerOptions) => {
	layer = { ...layer }

	const startPos = options.pointerStart
	const pos = options.pointer
	const delta = vec2.sub(vec2.create(), pos, startPos)
	const straighten = options.adjustments.cropStraighten
	const hasTransform = hasNonZeroTransform(options.adjustments)

	if (Math.abs(straighten) < 0.01 && !hasTransform) {
		layer.x -= delta[0] / 2
		layer.y -= delta[1] / 2

		// Limit crop position
		layer.x = clamp(layer.x, layer.width / 2, 1 - layer.width / 2)
		layer.y = clamp(layer.y, layer.height / 2, 1 - layer.height / 2)
	} else {
		// need correct aspect ratio for computation
		const orientedSize = options.imageSize
		const hwRatio = orientedSize[1] / orientedSize[0]
		const rotationAngle = radians(straighten)
		const dx = -delta[0] / 2
		const dy = (-delta[1] / 2) * hwRatio

		const initialBounds = layerToBounds(layer)
		const startCrop: vec4 = [
			initialBounds.left,
			initialBounds.top * hwRatio,
			initialBounds.right,
			initialBounds.bottom * hwRatio,
		]

		let dxdy: vec2
		if (!hasTransform) {
			// idea: imagine image is not rotated, and rotate crop and dx, dy; find limits of dx, dy from the unrotated image bounds.

			dxdy = getLimitedDxDyForRotation(dx, dy, startCrop, orientedSize, rotationAngle)
		} else {
			// idea: get transformed image bounds coordinates, move the crop until the first collision point, then slide along the edge.

			const transformedImageVertices = getTransformedImageVertices(
				options.adjustments,
				orientedSize,
				options.initialOrientation,
			)

			const imageCenter: vec2 = [0.5, 0.5 * hwRatio]
			const straightenedImageVertices = getRotatedCrop(
				[0, 0, 1, hwRatio],
				rotationAngle,
				imageCenter,
			)

			dxdy = getLimitedDxDyWithSlidingEdge(
				startCrop,
				dx,
				dy,
				transformedImageVertices,
				straightenedImageVertices,
			)
		}

		layer.x += dxdy[0]
		layer.y += dxdy[1] / hwRatio // scale back aspectRatio to 1
	}

	return layer
}

export const constrainCropStraighten = (
	adjustments: Pick<Adjustments, "crop" | "cropStraighten">,
	imageSize: vec2,
): Crop => {
	const layer = cropToLayer(adjustments.crop)
	const straighten = adjustments.cropStraighten
	const hwRatio = imageSize[1] / imageSize[0]
	const keepAspect = true

	// Overall idea: imagine the image is not rotated, and rotate crop;
	// find the vertice that is farest from the image bounds, use it as origin.
	// move the origin back to image bounds, and scale-down the crop if some other vertices are still outside.

	const rotationAngle = radians(straighten)
	const imageBounds = [
		[0, 1],
		[0, hwRatio],
	]

	const initialBounds = layerToBounds(layer)
	const startCrop: vec4 = [
		initialBounds.left,
		initialBounds.top * hwRatio,
		initialBounds.right,
		initialBounds.bottom * hwRatio,
	]
	const rotatedCrop = getRotatedCrop(startCrop, -rotationAngle, [0.5, 0.5 * hwRatio])

	const anchors = ["topLeft", "topRight", "bottomRight", "bottomLeft"]
	let maxPercent = 0
	let maxIndex = -1
	for (let i = 0; i < anchors.length; i++) {
		const s = getOutofBoundsPercent(rotatedCrop[i] as vec2, [0.5, 0.5 * hwRatio], imageBounds)
		if (s > maxPercent + 0.000001) {
			maxPercent = s
			maxIndex = i
		}
	}

	if (maxIndex === -1) {
		return layerToCrop(layer)
	}

	// Move the crop to make the vertice with maxIndex is back on bounds
	const [x, y] = rotatedCrop[maxIndex] as [number, number]
	const dx = (0.5 - x) * maxPercent
	const dy = (0.5 * hwRatio - y) * maxPercent
	if (dx || dy) {
		for (const element of rotatedCrop) {
			element[0] += dx
			element[1] += dy
		}

		const [fdx, fdy] = rotatePoint(dx, dy, rotationAngle, [0, 0]) as [number, number]
		layer.x += fdx
		layer.y += fdy / hwRatio // scale back aspectRatio to 1
	}

	// Scale down the crop to make sure every vertice is within bounds
	const originAnchor = anchors[maxIndex] as string
	const originPoint = anchorPoints[originAnchor]
	const rotatedOrigin = rotatedCrop[maxIndex] as vec2
	let [scaleX, scaleY] = getCropScalingPercent(
		rotatedCrop,
		rotatedOrigin,
		originPoint,
		null,
		null,
		imageBounds,
		true,
	) as [number, number]

	if (keepAspect) {
		scaleX = scaleY = Math.max(scaleX, scaleY)
	}

	if (scaleX || scaleY) {
		const origin = rotatePoint(rotatedOrigin[0], rotatedOrigin[1], rotationAngle, [
			0.5,
			0.5 * hwRatio,
		])
		origin[1] /= hwRatio
		scaleLayer(layer, [1 - scaleX, 1 - scaleY], origin)
	}

	return layerToCrop(layer)
}

function getCropScalingPercent(
	cropVertices,
	origin,
	originPoint,
	imageVertices1,
	imageVertices2,
	imageBounds,
	isImageUpright,
): vec2 {
	// Scale down the crop to make sure every vertice is within bounds
	const anchors = ["topLeft", "topRight", "bottomRight", "bottomLeft"]
	let scaleX = 0,
		scaleY = 0

	for (let i = 0; i < anchors.length; i++) {
		const anchor = anchors[i] as string
		const anchorPoint = anchorPoints[anchor]

		if (anchorPoint[0] === originPoint[0] && anchorPoint[1] === originPoint[1]) {
			continue
		}
		let s = 0
		if (isImageUpright) {
			s = getOutofBoundsPercent(cropVertices[i], origin, imageBounds)
		} else {
			const s1 = getOutofBoundsPercentForIrregularRect(
				cropVertices[i],
				origin,
				imageVertices1,
			)
			const s2 = getOutofBoundsPercentForIrregularRect(
				cropVertices[i],
				origin,
				imageVertices2,
			)
			s = Math.max(s1.maxPercent, s2.maxPercent)
		}

		const dx = anchorPoint[0] - originPoint[0]
		const dy = anchorPoint[1] - originPoint[1]
		const sx = dx !== 0 ? s : 0
		const sy = dy !== 0 ? s : 0

		scaleX = Math.max(scaleX, sx)
		scaleY = Math.max(scaleY, sy)
	}

	return [scaleX, scaleY]
}

export function hasNonZeroTransform(adjustments: Adjustments) {
	const keys = [
		"perspectiveVertical",
		"perspectiveHorizontal",
		"perspectiveRotate",
		"perspectiveScale",
		"perspectiveAspect",
		"perspectiveOffsetX",
		"perspectiveOffsetY",
	]
	const defaultAdjustments = getDefaultAdjustments()

	for (const key of keys) {
		if (adjustments[key] !== defaultAdjustments[key]) {
			return true
		}
	}

	return false
}

// This constrains crop for both straighten and perspective transform
export const constrainCrop = (
	adjustments: Adjustments,
	orientedSize: vec2,
	initialOrientation: number,
): Crop => {
	if (!hasNonZeroTransform(adjustments)) {
		return constrainCropStraighten(adjustments, orientedSize)
	}

	// Overall idea: find new coordinates of transformed image bounds (vertices),
	// find the crop-vertice that is farest from the image bounds, use it as origin.
	// move the origin back to image bounds, and scale-down the crop if some other vertices are still outside.

	const layer = cropToLayer(adjustments.crop)
	const [iw, ih] = orientedSize as [number, number]
	const hwRatio = ih / iw
	const keepAspect = true

	const initialBounds = layerToBounds(layer)
	const startCrop: vec4 = [
		initialBounds.left,
		initialBounds.top * hwRatio,
		initialBounds.right,
		initialBounds.bottom * hwRatio,
	]
	const [left, top, right, bottom] = startCrop
	const cropVertices: vec2[] = [
		[left, top],
		[right, top],
		[right, bottom],
		[left, bottom],
	]

	const transformedImageVertices = getTransformedImageVertices(
		adjustments,
		orientedSize,
		initialOrientation,
	)

	const imageCenter: vec2 = [0.5, 0.5 * hwRatio]
	const rotationAngle = radians(adjustments.cropStraighten)
	const straightenedImageVertices = getRotatedCrop([0, 0, 1, hwRatio], rotationAngle, imageCenter)

	const movingOrigin: vec2 = getOverlapCenter(
		transformedImageVertices,
		[0, 0, 1, hwRatio],
		rotationAngle,
	)

	let maxPercent = 0
	let maxIndex = -1
	for (let i = 0; i < cropVertices.length; i++) {
		const cropVertex = cropVertices[i] as vec2
		const s1 = getOutofBoundsPercentForIrregularRect(
			cropVertex,
			movingOrigin,
			transformedImageVertices,
		)
		const s2 = getOutofBoundsPercentForIrregularRect(
			cropVertex,
			movingOrigin,
			straightenedImageVertices,
		)
		const s = Math.max(s1.maxPercent, s2.maxPercent)
		if (s > maxPercent + 0.000001) {
			maxPercent = s
			maxIndex = i
		}
	}

	if (maxIndex === -1) {
		return layerToCrop(layer)
	}

	// Move the crop to make the vertice with maxIndex is back on bounds
	const [x, y] = cropVertices[maxIndex] as [number, number]
	const dx = (movingOrigin[0] - x) * maxPercent
	const dy = (movingOrigin[1] - y) * maxPercent
	if (dx || dy) {
		for (const cropVertex of cropVertices) {
			cropVertex[0] += dx
			cropVertex[1] += dy
		}

		layer.x += dx
		layer.y += dy / hwRatio // scale back aspectRatio to 1
	}

	// Scale down the crop to make sure every vertice is within bounds
	const anchors = ["topLeft", "topRight", "bottomRight", "bottomLeft"]
	const originAnchor = anchors[maxIndex] as string
	const origin = cropVertices[maxIndex] as vec2
	let [scaleX, scaleY] = getCropScalingPercent(
		cropVertices,
		origin,
		anchorPoints[originAnchor],
		transformedImageVertices,
		straightenedImageVertices,
		null,
		false,
	) as [number, number]

	if (keepAspect) {
		scaleX = scaleY = Math.max(scaleX, scaleY)
	}

	if (scaleX || scaleY) {
		origin[1] /= hwRatio
		scaleLayer(layer, [1 - scaleX, 1 - scaleY], origin)
	}

	return layerToCrop(layer)
}

export const handleCropRotate90 = (crop: Crop, direction: number): Crop => {
	const [left, top, width, height] = crop
	if (direction > 0) {
		// CW
		const x = 1 - top - height
		const y = left
		return [x, y, height, width]
	} else {
		const x = top
		const y = 1 - left - width
		return [x, y, height, width]
	}
}

export const handleCropFlip = (crop: Crop, isXAxis: boolean): Crop => {
	const [left, top, width, height] = crop
	if (isXAxis) {
		// flip horizontally on screen
		const x = 1 - left - width
		return [x, top, width, height]
	} else {
		// flip vertically on screen
		const y = 1 - top - height
		return [left, y, width, height]
	}
}

export const cropToLayer = (crop: vec4): Layer => {
	const [left, top, width, height] = crop as [number, number, number, number]

	return {
		x: left + width / 2,
		y: top + height / 2,
		width,
		height,
	}
}

export const layerToCrop = (layer: Layer): Crop => {
	// prettier-ignore
	return [
		layer.x - layer.width / 2,
		layer.y - layer.height / 2,
		layer.width,
		layer.height
	]
}

/**
 * Update crop for transforms if needed
 * Returns the updated crop if needed, null if no update was needed
 */
export const updateCropForTransforms = (
	currentAdjustments: Adjustments,
	imageSize: vec2,
	initialOrientation: number,
): Crop | null => {
	if (!hasNonZeroTransform(currentAdjustments)) {
		return null
	}

	const constrainedCrop = constrainCrop(currentAdjustments, imageSize, initialOrientation)

	if (!equal(constrainedCrop, currentAdjustments.crop)) {
		return constrainedCrop
	}

	return null
}

/**
 * Reset crop to default with smart constraint based on current transforms
 */
export const resetCropWithConstraint = (
	currentAdjustments: Adjustments,
	imageSize: vec2,
	initialOrientation: number,
) => {
	const defaults = getDefaultAdjustments()
	const { crop, cropRatio, cropStraighten, flipX, flipY, orientation } = defaults

	// If transforms are present, constrain the default crop to valid image bounds
	const finalCrop = hasNonZeroTransform(currentAdjustments)
		? constrainCrop(
				{
					...currentAdjustments,
					crop,
					cropRatio,
					cropStraighten,
					flipX,
					flipY,
					orientation,
				},
				imageSize,
				initialOrientation,
			)
		: crop

	return { crop: finalCrop, cropRatio, cropStraighten, flipX, flipY, orientation }
}
